import {
  type Table,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { type PropsWithChildren, createContext, useContext } from "react";

import usePagination from "@/components/shared/tables/pagination";
import useTableSorting from "@/components/shared/tables/sortingPersistence";
import { columns } from "./columns";

const ExportsTableInstanceContext = createContext<
  | {
      table: Table<any>;
    }
  | undefined
>(undefined);

export function useExportsTableInstanceContext() {
  const context = useContext(ExportsTableInstanceContext);
  if (context === undefined) {
    throw new Error(
      "useExportsTableInstanceContext must be within ExportsTableInstanceProvider",
    );
  }

  return context;
}

type Props = PropsWithChildren<{
  tableName: string;
  data: any;
}>;

export const ExportsTableInstanceProvider = ({
  data,
  children,
  tableName,
}: Props) => {
  const { sorting, onSortingChange } = useTableSorting(tableName);
  const { pagination, onPaginationChange } = usePagination();

  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => row.invoiceId,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: onSortingChange,
    filterFns: {},
    onPaginationChange: onPaginationChange,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableColumnResizing: true,
    enableMultiRowSelection: true,
    autoResetPageIndex: false,
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <ExportsTableInstanceContext.Provider
      value={{
        table,
      }}
    >
      {children}
    </ExportsTableInstanceContext.Provider>
  );
};
